import React, { useContext, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import {
    CreateZahtjevRequest,
    CreateZahtjevResponse,
    useRegisterUser,
    ZahtjevValidation
} from "../../api/RegistrationApi";
import { ZahtjevContext } from "../../common/context/ZahtjevProvider";
import { Button } from "../common/Button";
import Switch from "../common/Switch";
import { useNotification } from "../../common/context/NotificationProvider";
import { ERROR_MESSAGE_NOTIFICATION } from "../../common/constants/notificationConstants";
import { REGISTRATION_STATUS } from "../../common/constants/navigationConstants";
import { Spinner } from "../common/Spinner";
import InputField from "../common/InputField";
import { useMediaQuery } from "react-responsive";
import { ANALYTIC_USER_ID } from "../../common/constants/persistentDataConstants";
import { Trans, useTranslation } from "react-i18next";
import { SupportedLanguage } from "../../i18n";
import { scrollToFirstError } from "../../util/formUtil";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY!;
const OIB_LENGTH = 11;

const PDF_FILENAME_HR = 'Informacije o obradi Vaših osobnih podataka.pdf';
const PDF_URL_HR = `/kyc2025/data/${PDF_FILENAME_HR}`;

const PDF_FILENAME_EN = 'Information on data collection for electronic channels.pdf';
const PDF_URL_EN = `/kyc2025/data/${PDF_FILENAME_EN}`;

type OIBFormErrors = {
    oib?: string;
    captcha?: string;
    confirm?: string;
}

const OIBForm = () => {
    const { t, i18n } = useTranslation();
    const lng = i18n.resolvedLanguage as SupportedLanguage;
    const [oib, setOib] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [hasDownloaded, setHasDownloaded] = useState(false);
    const [error, setError] = useState<OIBFormErrors | undefined>();
    const {
        handleSuccessFullRegistration,
        getStatus,
        restoreContext,
        gdprCookieInformation
    } = useContext(ZahtjevContext);
    const currentStatus = getStatus();
    const pageStatus = REGISTRATION_STATUS;
    const { notify } = useNotification();
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const { isPending: isLoading, mutate: registerUserMutation } = useRegisterUser();
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ maxWidth: 767 });

    const oibValidation = (value: string) => {
        let error: string | undefined = undefined;

        if (value.length > 0 && value.length !== OIB_LENGTH) {
            error = "registration-page.oib-field.validation-error.length";
        }

        const oibRegex = /^[0-9]+$/;
        if (value && !oibRegex.test(value)) {
            error = "common.validation-error.type.number";
        }

        setError(prevState => {
            return {
                ...prevState,
                oib: error
            }
        });
    }

    const setOibValue = (value: string) => {
        if (value.length <= 11) {
            oibValidation(value);
            setOib(value);
        }
    }

    const handleRecaptchaChange = (token: string | null) => {
        setError(prevState => {
            return {
                ...prevState,
                captcha: !token ? "registration-page.captcha.validation-error.general" : undefined
            }
        });

        if (!isMobile) {
            return;
        }

        if (token) {
            setCaptchaToken(token);
        } else {
            recaptchaRef.current?.reset();
        }
    };

    const handleUserRegistration = (token: string) => {
        const request: CreateZahtjevRequest = {
            oib: oib,
            recaptchaToken: token,
            consentData: {
                analyticsConsent: gdprCookieInformation?.areAnalyticsCookiesAllowed ?? null,
                marketingConsent: gdprCookieInformation?.areMarketingCookiesAllowed ?? null,
                analyticsUserIdFromSession: sessionStorage.getItem(ANALYTIC_USER_ID),
            }
        }

        registerUserMutation(request, {
            onSuccess: (response) => {
                if ((response as CreateZahtjevResponse)?.zahtjevId && ((response as CreateZahtjevResponse)?.accessToken)) {
                    handleSuccessFullRegistration(oib, response as CreateZahtjevResponse);
                    navigate('/upload');
                } else {
                    recaptchaRef.current?.reset();

                    if ((response as ZahtjevValidation)?.status) {
                        const status = (response as ZahtjevValidation).status;

                        if (status === "INVALID_RECAPTCHA") {
                            setError(prevState => {
                                return {
                                    ...prevState,
                                    captcha: "registration-page.captcha.validation-error.general"
                                }
                            });
                        } else if (status === "INVALID_DATA") {
                            setError(prevState => {
                                return {
                                    ...prevState,
                                    oib: "registration-page.oib-field.validation-error.general"
                                }
                            });
                        } else if (status === "INVALID_KON_OC") {
                            navigate("/redirect")
                        } else {
                            notify(ERROR_MESSAGE_NOTIFICATION, "error");
                        }

                        return;
                    }
                }
            },
            onError: () => {
                recaptchaRef.current?.reset();
                notify(ERROR_MESSAGE_NOTIFICATION, "error");
            },
        });
    }

    const handleConfirmChange = (value: boolean) => {
        setIsConfirmed(value);
        setError(prevState => {
            return {
                ...prevState,
                confirm: value ? undefined : prevState?.confirm
            }
        });
    };

    const handleDownload = () => {
        try {
            const link = document.createElement('a');
            const pdfUrl = lng === 'en' ? PDF_URL_EN : PDF_URL_HR;
            const pdfFilename = lng === 'en' ? PDF_FILENAME_EN : PDF_FILENAME_HR;
            link.href = pdfUrl;
            link.download = pdfFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setHasDownloaded(true);
            setError(prevState => {
                return {
                    ...prevState,
                    document: undefined
                }
            });
        } catch (err) {
            console.error("Download failed:", err);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setError((prevState) => {
            return {
                ...prevState,
                oib: (oib.length !== OIB_LENGTH) ? "registration-page.oib-field.validation-error.length" : undefined,
                confirm: !isConfirmed ? "registration-page.data-collection-policy.download-document.error.read-confirmation" : undefined,
            }
        })

        if ((oib.length !== OIB_LENGTH) || !hasDownloaded || !isConfirmed) {
            scrollToFirstError();
            return;
        }

        try {
            if (isMobile) {
                if (captchaToken) {
                    handleUserRegistration(captchaToken);
                    return;
                } else {
                    setError((prevState) => {
                        return {
                            ...prevState,
                            captcha: "registration-page.captcha.validation-error.general",
                        }
                    })
                }
            } else {
                await recaptchaRef.current?.executeAsync()
                    .then((token) => {
                        if (!token) {
                            recaptchaRef.current?.reset();
                            return;
                        }
                        handleUserRegistration(token);
                    }).catch((error) => {
                        console.error("reCAPTCHA execution failed:", error);
                        setError((prevState) => {
                            return {
                                ...prevState,
                                captcha: "registration-page.captcha.validation-error.general",
                            }
                        })
                    });
            }
        } catch (recaptchaError) {
            console.error("reCAPTCHA execution failed:", recaptchaError);
            setError((prevState) => {
                return {
                    ...prevState,
                    captcha: "registration-page.captcha.validation-error.general",
                }
            })
        }
    };

    useEffect(() => {
        restoreContext(pageStatus);
    }, []);

    if (parseInt(currentStatus) < parseInt(pageStatus)) {
        return <Spinner className="mt-60" size={40} />
    }

    return (
      <form
        onSubmit={handleSubmit}
        className="w-full"
        noValidate
        autoComplete="off"
      >
          <div className="mb-4">
              <InputField
                className="md:w-2/3"
                name="OIB"
                label={t("registration-page.oib-field.label")}
                error={t(error?.oib as string)}
                defaultValue={oib}
                handleValueChange={setOibValue}
                handleValidation={oibValidation}
                type="oib"
              />
          </div>

          <ReCAPTCHA
            key={i18n.resolvedLanguage}
            ref={recaptchaRef}
            sitekey={SITE_KEY}
            onChange={handleRecaptchaChange}
            size={isMobile ? "compact" : "invisible"}
            badge="inline"
            hl={i18n.resolvedLanguage}
          />
          {error?.captcha && (
            <p className="text-red-500 text-sm mt-1" role="alert">
                {t(error.captcha)}
            </p>
          )}

          <p className="text-sm mt-8 mb-4 text-gray-700">
            <Trans
              i18nKey="registration-page.data-collection-policy.instructions"
              components={{
                document: <a
                  href={i18n.resolvedLanguage === 'en' ? "https://www.otpbanka.hr/en/data-protection" : "https://www.otpbanka.hr/hr/zastita-osobnih-podataka"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#52ae30] font-medium underline hover:text-green-700 ml-1"
                />,
                b: <strong />
              }}
            />
          </p>

          <Button className="mb-4" type="button" text={t("registration-page.data-collection-policy.download-document.button")} onClick={handleDownload} />

          <div className="flex mb-6">
              <Switch
                name="isConfirmed"
                defaultValue={isConfirmed}
                disabled={!hasDownloaded}
                handleValueChange={handleConfirmChange}
                label={
                    <span className={`text-sm font-semibold leading-snug ${hasDownloaded ? "text-gray-700" : "text-gray-400"}`}>
                        {t("registration-page.data-collection-policy.download-document.confirmation")}
                    </span>
                }
              />
          </div>

          {error?.confirm && (
            <p className="text-red-500 text-sm -mt-4 mb-6" role="alert" data-field-error="true">
                {t(error.confirm)}
            </p>
          )}

          <div className="flex justify-center md:justify-start">
              <Button type="submit" text={t("common.button-next")} disabled={isLoading} />
          </div>
      </form>
    );
};

export default OIBForm;
